import { normalizeLangKey } from '@src/utility/bcp47';
import { isOriginalLang } from '@src/utility/captionLanguage';

import type { KaraokeData } from '../../types/karaoke';

/**
 * PLAT-10788 — pick the persisted karaoke variant for a language from a
 * per-language map (`karaokeDataByLanguage`).
 *
 * - **Translated** (`isOriginal: false`): look up `normalizeLangKey(langCode)`
 *   — translated entries are keyed by their canonical BCP-47 code.
 * - **Original** (`isOriginal: true`): return the entry whose default
 *   `KaraokeConfig.language` is a source sentinel (`isOriginalLang`). The source
 *   variant is stored under a real BCP-47 key (the source code) but its config
 *   language stays `'original'`/`'english'`/empty, so scanning by config avoids
 *   needing the transcript language (which the publish path can't reach).
 *
 * Returns `null` when the map is absent/empty or has no matching entry — callers
 * fall back to the single active blob (and produce on a miss).
 *
 * Store-free (unlike `KaraokeStore.hydrateLoadedByLanguage`) so both the publish
 * source builder and the live staleness fingerprint hook can share it.
 */
export function selectKaraokeVariantForLanguage(
  byLanguage: Record<string, KaraokeData> | undefined,
  opts: { isOriginal: boolean; langCode: string },
): KaraokeData | null {
  return (
    selectKaraokeVariantEntryForLanguage(byLanguage, opts)?.variant ?? null
  );
}

/**
 * PLAT-11410 — like {@link selectKaraokeVariantForLanguage}, but also returns
 * the map KEY the variant is stored under. The key is the variant's canonical
 * per-language key — source variants are stored under their real BCP-47
 * source code even though their config carries the `'original'` sentinel —
 * which the publish-time font correction needs to resolve the script.
 */
export function selectKaraokeVariantEntryForLanguage(
  byLanguage: Record<string, KaraokeData> | undefined,
  opts: { isOriginal: boolean; langCode: string },
): { key: string; variant: KaraokeData } | null {
  if (!byLanguage) return null;

  if (opts.isOriginal) {
    for (const [key, variant] of Object.entries(byLanguage)) {
      const configLanguage = variant.defaultConfigId
        ? variant.configs.get(variant.defaultConfigId)?.language
        : undefined;
      if (isOriginalLang(configLanguage)) return { key, variant };
    }
    return null;
  }

  const key = normalizeLangKey(opts.langCode);
  const variant = byLanguage[key];
  return variant ? { key, variant } : null;
}
